import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 360;
const MARGIN = { top: 30, right: 30, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function MonteCarloFanChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.historical || !data.historical.dates || data.historical.dates.length === 0) return;
    if (!data.forecast || !data.forecast.dates || data.forecast.dates.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const historical = data.historical;
    const forecast = data.forecast;

    // Parse all dates
    const histDates = historical.dates.map((d) => new Date(d));
    const fcDates = forecast.dates.map((d) => new Date(d));
    const allDates = [...histDates, ...fcDates];

    // Build Y domain from all values
    const allValues = [
      ...historical.values,
      ...forecast.p10,
      ...forecast.p90,
      ...forecast.median,
    ];
    if (forecast.samplePaths) {
      forecast.samplePaths.forEach((path) => {
        allValues.push(...path);
      });
    }

    const priceMin = d3.min(allValues);
    const priceMax = d3.max(allValues);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(allDates))
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(allDates.length, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // ── Forecast fan bands ──

    // Build bridge point: last historical value connects to first forecast value
    const lastHistDate = histDates[histDates.length - 1];
    const lastHistValue = historical.values[historical.values.length - 1];

    // P10-P90 band (outer, lighter)
    const outerBandData = [
      { date: lastHistDate, lower: lastHistValue, upper: lastHistValue },
      ...fcDates.map((d, i) => ({
        date: d,
        lower: forecast.p10[i],
        upper: forecast.p90[i],
      })),
    ];

    const areaGen = d3.area()
      .x((d) => x(d.date))
      .y0((d) => y(d.lower))
      .y1((d) => y(d.upper))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(outerBandData)
      .attr('d', areaGen)
      .attr('fill', '#93c5fd')
      .attr('fill-opacity', 0.2)
      .attr('stroke', 'none');

    // P25-P75 band (inner, darker)
    const innerBandData = [
      { date: lastHistDate, lower: lastHistValue, upper: lastHistValue },
      ...fcDates.map((d, i) => ({
        date: d,
        lower: forecast.p25[i],
        upper: forecast.p75[i],
      })),
    ];

    g.append('path')
      .datum(innerBandData)
      .attr('d', areaGen)
      .attr('fill', '#60a5fa')
      .attr('fill-opacity', 0.3)
      .attr('stroke', 'none');

    // ── Sample paths (thin, semi-transparent) ──
    if (forecast.samplePaths && forecast.samplePaths.length > 0) {
      const sampleLineGen = d3.line()
        .x((d) => x(d.date))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      forecast.samplePaths.forEach((path) => {
        const pathData = [
          { date: lastHistDate, value: lastHistValue },
          ...path.map((v, i) => ({ date: fcDates[i], value: v })),
        ];

        g.append('path')
          .datum(pathData)
          .attr('d', sampleLineGen)
          .attr('fill', 'none')
          .attr('stroke', '#3b82f6')
          .attr('stroke-width', 0.8)
          .attr('stroke-opacity', 0.2);
      });
    }

    // ── Historical line (solid dark blue) ──
    const histLine = d3.line()
      .x((d, i) => x(histDates[i]))
      .y((d) => y(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(historical.values)
      .attr('d', histLine)
      .attr('fill', 'none')
      .attr('stroke', '#1e40af')
      .attr('stroke-width', 2.5);

    // Dots on historical line
    historical.values.forEach((v, i) => {
      if (v == null) return;
      g.append('circle')
        .attr('cx', x(histDates[i]))
        .attr('cy', y(v))
        .attr('r', 3)
        .attr('fill', '#1e40af');
    });

    // ── Median forecast line (dashed blue) ──
    const medianData = [
      { date: lastHistDate, value: lastHistValue },
      ...forecast.median.map((v, i) => ({ date: fcDates[i], value: v })),
    ];

    const medianLineGen = d3.line()
      .x((d) => x(d.date))
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(medianData)
      .attr('d', medianLineGen)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,3');

    // ── Vertical dashed "Today" marker at forecast start ──
    const boundaryX = x(lastHistDate);

    g.append('line')
      .attr('x1', boundaryX)
      .attr('x2', boundaryX)
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    g.append('text')
      .attr('x', boundaryX)
      .attr('y', -8)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#6b7280')
      .text('Today');

    // ── Crosshair and hover overlay ──
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const allParsedDates = [...histDates, ...fcDates];
    const n = allParsedDates.length;

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const dateAtMouse = x.invert(mx);
        const bisect = d3.bisector((d) => d).left;
        const timestamps = allParsedDates.map((d) => d.getTime());
        let idx = bisect(timestamps, dateAtMouse.getTime());
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = x(allParsedDates[idx]);
        crosshair
          .attr('x1', cx)
          .attr('x2', cx)
          .style('display', null);

        const histLen = histDates.length;
        const isHistorical = idx < histLen;

        if (isHistorical) {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'historical',
            date: historical.dates[idx],
            value: historical.values[idx],
          });
        } else {
          const fcIdx = idx - histLen;
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'forecast',
            date: forecast.dates[fcIdx],
            median: forecast.median[fcIdx],
            p10: forecast.p10[fcIdx],
            p25: forecast.p25[fcIdx],
            p75: forecast.p75[fcIdx],
            p90: forecast.p90[fcIdx],
          });
        }
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.historical || !data.historical.dates || data.historical.dates.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.historical || !data.historical.dates || data.historical.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Monte Carlo Price Forecast</div>
          <div style={styles.subtitle}>Simulated future price paths based on historical volatility</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No simulation data available
        </div>
      </div>
    );
  }

  const simCount = data.simulations || 0;
  const fieldLabel = data.field || 'price';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Monte Carlo Price Forecast</div>
        <div style={styles.subtitle}>
          Simulated future price paths based on historical volatility
          {simCount > 0 && (
            <span style={{ marginLeft: '8px', color: '#6b7280', fontWeight: 600 }}>
              ({simCount.toLocaleString()} simulations &middot; {fieldLabel})
            </span>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#1e40af" strokeWidth="2.5" />
          </svg>
          <span>Historical</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" strokeDasharray="4,2" />
          </svg>
          <span>Median Forecast</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#60a5fa" fillOpacity="0.3" rx="2" />
          </svg>
          <span>P25-P75</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#93c5fd" fillOpacity="0.2" rx="2" />
          </svg>
          <span>P10-P90</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#3b82f6" strokeWidth="0.8" strokeOpacity="0.3" />
          </svg>
          <span>Sample Paths</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.type === 'historical' ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Price</span>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>
                ${tooltip.value != null ? tooltip.value.toFixed(2) : '--'}
              </span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>P90</span>
                <span style={{ fontWeight: 600 }}>
                  ${tooltip.p90 != null ? tooltip.p90.toFixed(2) : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>P75</span>
                <span style={{ fontWeight: 600 }}>
                  ${tooltip.p75 != null ? tooltip.p75.toFixed(2) : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#2563eb', fontWeight: 600 }}>Median</span>
                <span style={{ fontWeight: 600, color: '#2563eb' }}>
                  ${tooltip.median != null ? tooltip.median.toFixed(2) : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>P25</span>
                <span style={{ fontWeight: 600 }}>
                  ${tooltip.p25 != null ? tooltip.p25.toFixed(2) : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>P10</span>
                <span style={{ fontWeight: 600 }}>
                  ${tooltip.p10 != null ? tooltip.p10.toFixed(2) : '--'}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
